/**
 * Shared date/time formatting for session surfaces.
 *
 * The live-sessions list, the admin dashboard, and the public join page all
 * render a session's created time. They previously each carried their own copy
 * of these helpers (with subtly different input types — `number` vs `Date`),
 * which let the join page drift and ship with no timestamp at all (MP-165).
 * Centralising here keeps every surface consistent and accepts whatever shape
 * the source hands us (epoch ms over the wire, `Date` after superjson decode).
 */

import { format, formatDistanceToNow } from 'date-fns'
import { cs as csLocale } from 'date-fns/locale'

/** Coerce a Date, epoch-ms number, or nullish value into a valid Date or null. */
function toDate(value: Date | number | null | undefined): Date | null {
	if (value === null || value === undefined) return null
	const date = value instanceof Date ? value : new Date(value)
	return Number.isNaN(date.getTime()) ? null : date
}

function dateFnsLocale(locale: string) {
	return locale.startsWith('cs') ? csLocale : undefined
}

/**
 * Relative time with suffix — e.g. "5 minutes ago" / "před 5 minutami".
 * Returns '' for nullish or invalid input so callers can render it directly.
 */
export function relativeTime(value: Date | number | null | undefined, locale: string): string {
	const date = toDate(value)
	if (!date) return ''
	return formatDistanceToNow(date, { addSuffix: true, locale: dateFnsLocale(locale) })
}

/**
 * Absolute date-time — e.g. "2026-06-04 22:05". Returns '' for nullish/invalid.
 */
export function formatDate(value: Date | number | null | undefined, locale: string): string {
	const date = toDate(value)
	if (!date) return ''
	return format(date, 'yyyy-MM-dd HH:mm', { locale: dateFnsLocale(locale) })
}
